import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Globe, Navigation, MessageCircle, Send, ArrowUpRight, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import Accreditations from "@/components/Accreditations";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CONTACT, WHATSAPP_LINK, MAPS_EMBED, MAPS_DIRECTIONS, FORMSPREE_ENDPOINT,
} from "@/lib/site";

const TRAVEL_TYPES = ["Air Ticketing", "Visa Assistance", "Travel Insurance", "Executive Travel", "Tourism / Family Visit", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", travelType: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const body = () =>
    `Corporate Enquiry\n-----------------\nName: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\nTravel Type: ${form.travelType}\nMessage: ${form.message}`;

  const sendEmail = () => {
    window.location.href = `mailto:${CONTACT.emails[0]}?subject=${encodeURIComponent("Corporate Enquiry: Fly Gowell")}&body=${encodeURIComponent(body())}`;
  };
  const sendWhatsApp = () => window.open(WHATSAPP_LINK(body()), "_blank");

  const submitFormspree = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...form, _subject: "Corporate Enquiry: Fly Gowell" }),
      });
      if (res.ok) {
        trackEvent("generate_lead", { form: "contact", travel_type: form.travelType });
        toast.success("Message sent! We'll reach out shortly.");
        setForm({ name: "", email: "", company: "", travelType: "", message: "" });
      } else {
        toast.error("Could not send. Please try Email or WhatsApp instead.");
      }
    } catch {
      toast.error("Could not send. Please try Email or WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-40 pb-16 md:pt-48 md:pb-20">
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-brand-blue/[0.06] via-transparent to-brand-green/[0.08]" />
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 relative z-10">
          <Overline>Contact Us</Overline>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 max-w-3xl text-4xl md:text-6xl font-bold tracking-tight text-brand-blue leading-[1.05]"
          >
            Let&apos;s plan your next corporate journey.
          </motion.h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-24 md:pb-28">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Left: details */}
          <Reveal className="lg:col-span-5">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-blue/5 text-brand-blue"><MapPin size={20} /></div>
                <div><p className="font-heading font-semibold text-brand-blue">Office</p><p className="text-brand-slate">{CONTACT.address}</p></div>
              </div>
              <div className="flex gap-4">
                <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-blue/5 text-brand-blue"><Phone size={20} /></div>
                <div><p className="font-heading font-semibold text-brand-blue">Phone</p>
                  <p className="text-brand-slate">{CONTACT.phones.map((p, i) => (<a key={p} href={`tel:+91${p}`} className="hover:text-brand-ocean">{p}{i < CONTACT.phones.length - 1 ? ", " : ""}</a>))}</p>
                  {CONTACT.landline && (
                    <p className="text-brand-slate mt-1"><span className="text-xs uppercase tracking-widest text-brand-slate/70 mr-1">Landline</span><a href={`tel:${CONTACT.landline.replace(/[^+\d]/g, "")}`} className="hover:text-brand-ocean">{CONTACT.landline}</a></p>
                  )}
                </div>
              </div>
              <div className="flex gap-4">
                <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-blue/5 text-brand-blue"><Mail size={20} /></div>
                <div><p className="font-heading font-semibold text-brand-blue">Email</p>
                  <p className="text-brand-slate">{CONTACT.emails.map((em, i) => (<a key={em} href={`mailto:${em}`} className="hover:text-brand-ocean break-all">{em}{i < CONTACT.emails.length - 1 ? ", " : ""}</a>))}</p></div>
              </div>
              <div className="flex gap-4">
                <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-blue/5 text-brand-blue"><Globe size={20} /></div>
                <div><p className="font-heading font-semibold text-brand-blue">Website</p><a href={`https://${CONTACT.website}`} target="_blank" rel="noreferrer" className="text-brand-slate hover:text-brand-ocean">{CONTACT.website}</a></div>
              </div>

              <a href={WHATSAPP_LINK("Hi Fly Gowell, I'd like to enquire about your corporate travel services.")} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-green px-6 py-3.5 font-semibold text-white hover:brightness-110 transition-all">
                <MessageCircle size={18} /> Chat on WhatsApp
              </a>

              {/* Map */}
              <div className="overflow-hidden rounded-2xl border border-brand-blue/10">
                <iframe title="Fly Gowell Office Location" src={MAPS_EMBED} data-testid="contact-map" className="w-full h-64" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
              <a href={MAPS_DIRECTIONS} target="_blank" rel="noreferrer" data-testid="contact-directions" className="inline-flex items-center gap-2 rounded-full border-2 border-brand-blue px-6 py-3 font-medium text-brand-blue hover:bg-brand-blue hover:text-white transition-colors">
                <Navigation size={17} /> Open in Google Maps / Get Directions
              </a>
            </div>
          </Reveal>

          {/* Right: form */}
          <Reveal delay={0.15} className="lg:col-span-7">
            <form onSubmit={submitFormspree} data-testid="contact-form" className="rounded-3xl border border-brand-blue/8 bg-white p-8 md:p-10">
              <h2 className="text-2xl font-heading font-semibold text-brand-blue">Send us a message</h2>
              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                <div><Label className="text-brand-slate">Name</Label><Input data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className="mt-1.5" /></div>
                <div><Label className="text-brand-slate">Email</Label><Input data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className="mt-1.5" /></div>
                <div><Label className="text-brand-slate">Company</Label><Input data-testid="contact-company" value={form.company} onChange={set("company")} placeholder="Company name" className="mt-1.5" /></div>
                <div>
                  <Label className="text-brand-slate">Travel Type</Label>
                  <Select value={form.travelType} onValueChange={(v) => setForm((f) => ({ ...f, travelType: v }))}>
                    <SelectTrigger data-testid="contact-travel-type" className="mt-1.5"><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent>
                      {TRAVEL_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-6"><Label className="text-brand-slate">Message</Label><Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="How can we help?" rows={4} className="mt-1.5" /></div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button type="submit" disabled={sending} data-testid="contact-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-6 py-3.5 font-semibold text-white hover:bg-brand-ocean transition-colors disabled:opacity-60">
                  <Send size={17} /> {sending ? "Sending..." : "Send Message"}
                </button>
                <button type="button" onClick={sendEmail} data-testid="contact-email-btn" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand-blue px-6 py-3.5 font-semibold text-brand-blue hover:bg-brand-blue hover:text-white transition-colors">
                  <Mail size={17} /> Send by Email
                </button>
                <button type="button" onClick={sendWhatsApp} data-testid="contact-whatsapp-form-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green px-6 py-3.5 font-semibold text-white hover:brightness-110 transition-all">
                  <MessageCircle size={17} /> Send via WhatsApp
                </button>
              </div>

              <div className="mt-8 border-t border-brand-blue/10 pt-6 flex flex-col sm:flex-row gap-3">
                <a href={`mailto:${CONTACT.emails[0]}?subject=${encodeURIComponent("Request Corporate Onboarding: Fly Gowell")}`} data-testid="cta-onboarding" className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-green px-6 py-3.5 font-semibold text-white hover:brightness-110 transition-all">
                  Request Corporate Onboarding <ArrowUpRight size={17} />
                </a>
                <a href={WHATSAPP_LINK("Hi Fly Gowell, I'd like to book a call to discuss corporate travel.")} target="_blank" rel="noreferrer" data-testid="cta-book-call" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand-ocean px-6 py-3.5 font-semibold text-brand-ocean hover:bg-brand-ocean hover:text-white transition-colors">
                  <CalendarCheck size={17} /> Book a Call
                </a>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-brand-blue/5 bg-white py-16">
        <Accreditations />
      </section>
    </div>
  );
}
